import { CommonModule } from '@angular/common';
import { Component, inject, input, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { WizardStepComponent } from '@app/components/wizard/wizard-step/wizard-step.component';
import {
  StepType,
  WizardComponent,
  type WizardStep,
} from '@app/components/wizard/wizard.component';
import type {
  ProjectAdminResponse,
  ProjectBaseRequest,
} from '@app/models/admin.model';
import {
  getNewProjectFormGroup,
  type NewProjectFormGroup,
} from '@app/pages/new-project-idea/new-project-form/new-project-form.helper';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { StepSectionType } from '@app/stores/project-setup/steps';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  getAdminProjectIdeaFormGroup,
  type AdminProjectIdeaFormGroup,
} from '../project-admin-idea-step/admin-project-idea.form';
import { ProjectAdminIdeaStepComponent } from '../project-admin-idea-step/project-admin-idea-step.component';
import { ProjectIdeaFieldsComponent } from '../project-idea-fields/project-idea-fields.component';

@Component({
  selector: 'app-project-idea-base-wizard',
  standalone: true,
  imports: [
    CommonModule,
    WizardComponent,
    WizardStepComponent,
    ProjectIdeaFieldsComponent,
    ProjectAdminIdeaStepComponent,
    TranslatePipe,
    ButtonComponent,
  ],
  templateUrl: './project-idea-base-wizard.component.html',
  styleUrl: './project-idea-base-wizard.component.scss',
})
export class ProjectIdeaBaseWizardComponent {
  project = input<ProjectAdminResponse | null>(null);
  loading = input(false);

  private readonly fb = inject(FormBuilder);
  private readonly service = inject(ProjectAdminService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  // 2 step: Admin + Basic
  readonly form = this.fb.nonNullable.group({
    admin: getAdminProjectIdeaFormGroup(this.fb),
    basic: getNewProjectFormGroup(this.fb),
  }) as FormGroup<{
    admin: FormGroup<AdminProjectIdeaFormGroup>;
    basic: FormGroup<NewProjectFormGroup>;
  }>;

  steps: WizardStep<StepSectionType>[] = [
    {
      title: 'projectWizard.adminForm.formTitle',
      isActive: true,
      stepType: StepType.available,
      sectionType: StepSectionType.admin,
    },
    {
      title: 'general.basicData',
      isActive: false,
      stepType: StepType.available,
      sectionType: StepSectionType.baseData,
    },
  ];

  private activeIndex = 0;
  readonly saving = signal(false);

  setActive(index: number): void {
    this.activeIndex = index;
    this.steps = this.steps.map((s, i) => ({ ...s, isActive: i === index }));
  }
  isActive(i: number): boolean {
    return this.activeIndex === i;
  }

  cancel(): void {
    const id = this.project()?.id ?? '';
    this.router.navigate(['/admin/project-ideas', id]);
  }

  save(): void {
    const project = this.project();
    if (!project || this.form.invalid) return;

    const admin = this.form.controls.admin.value;
    const basic = this.form.controls.basic.value;

    const body: ProjectBaseRequest = {
      contactUserId: admin.contactId ?? null,
      foundingMode: admin.foundingMode ?? project.foundingMode,
      loanTerm: admin?.loanTerm ?? project.foundingMode ?? 0,
      classificationId:
        admin.investmentClassification ?? project.classification.id,

      interestMin: project.interestMin ?? null,
      interestMax: project.interestMax ?? null,
      yieldMin: project.yieldMin ?? null,
      yieldMax: project.yieldMax ?? null,

      startCollectionDate:
        admin?.startCollectionDate ?? project.startCollectionDate ?? null,
      endCollectionDate:
        admin?.endCollectionDate ?? project.endCollectionDate ?? null,

      loanTypePaymentStart:
        admin?.loanTypePaymentStart ?? project.loanTypePaymentStart ?? null,
      loanTypePaymentEnd:
        admin?.loanTypePaymentEnd ?? project.loanTypePaymentEnd ?? null,

      name: basic?.projectTitle ?? project.name ?? null,
      description: basic?.projectIntroduction ?? project?.description ?? null,
      country: basic?.country ?? project.country ?? null,
      city: basic?.city ?? project.city ?? null,
      currency: basic?.collectionCurrency ?? project.currency ?? null,
    };
    this.saving.set(true);
    this.service
      .updateProjectAdmin(project.id, body)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.toast.success('Saved successfully');
          this.router.navigate(['/admin/project-ideas', project.id]);
        },
        error: (err) => {
          this.toast.error('Save failed');
          console.error(err);
        },
      });
  }
}
